import type { ChatIntent, QuickQuestion } from "./types";

const QUICK_QUESTIONS: QuickQuestion[] = [
  {
    id: "best-job-today",
    label: "Vilket är det bästa jobbet för mig att söka idag?",
    intent: "best_job_today",
  },
  {
    id: "best-match",
    label: "Vilket jobb är min bästa match?",
    intent: "best_match",
  },
  {
    id: "search-jobs",
    label: "Hur söker jag jobb?",
    intent: "search_jobs",
  },
  {
    id: "my-applications",
    label: "Vilka jobb har sökts åt mig?",
    intent: "my_applications",
  },
  {
    id: "applied-status",
    label: "Har jag redan sökt detta jobbet?",
    intent: "applied_status",
  },
  {
    id: "why-job",
    label: "Varför visas det här jobbet?",
    intent: "why_this_job",
  },
  {
    id: "can-apply",
    label: "Kan jag söka det här jobbet?",
    intent: "can_apply",
  },
];

const EXACT_MATCHES = new Map<string, ChatIntent>(
  QUICK_QUESTIONS.map((q) => [normalizeText(q.label), q.intent]),
);

const INTENT_RULES: Array<{ intent: ChatIntent; patterns: RegExp[] }> = [
  {
    intent: "best_job_today",
    patterns: [
      /bästa.*jobbet.*idag/,
      /vilket.*job.*söka.*idag/,
      /borde.*söka.*idag/,
      /best.*job.*today/,
      /best.*job.*apply/,
    ],
  },
  {
    intent: "my_applications",
    patterns: [
      /vilka jobb.*sökts åt/,
      /vilka jobb har (du |agenten )?(sökt|sökt åt|ansökt) (åt )?(mig|sanna)/,
      /vilka jobb har (jag )?sökt/,
      /vilka jobb.*senaste (timmarna|timen|ansökningar)/,
      /senaste (ansökningar|timmarna|timen)/,
      /vilka jobb.*(för mig|till mig)/,
      /which jobs.*(applied|sent).*(for me|on my behalf)/,
    ],
  },
  {
    intent: "best_match",
    patterns: [
      /bästa.*match/,
      /mest.*match/,
      /best.*match/,
      /top match/,
      /highest.*match/,
    ],
  },
  {
    intent: "my_applications",
    patterns: [
      /vilka jobb.*sökts åt/,
      /vilka jobb har (du |agenten )?(sökt|sökt åt|ansökt) (åt )?(mig|sanna)/,
      /vilka jobb.*senaste (timmarna|timen|ansökningar)/,
      /senaste (ansökningar|timmarna|timen)/,
      /vilka jobb.*(för mig|till mig)/,
    ],
  },
  {
    intent: "applied_status",
    patterns: [
      /har.*redan.*sökt/,
      /redan.*sökt.*(detta|det här) jobb/,
      /har.*ansökt/,
      /redan.*sökt/,
      /har.*ansökt/,
      /redan.*ansökt/,
      /already.*applied/,
      /have.*applied/,
      /did i apply/,
    ],
  },
  {
    intent: "why_this_job",
    patterns: [
      /varför.*visas/,
      /varför.*jobbet/,
      /varför.*annons/,
      /why.*show/,
      /why.*this job/,
    ],
  },
  {
    intent: "can_apply",
    patterns: [
      /kan.*söka/,
      /kan.*ansöka/,
      /får.*söka/,
      /can.*apply/,
      /should i apply/,
      /eligible.*apply/,
    ],
  },
  {
    intent: "next_steps",
    patterns: [
      /vad.*göra.*nu/,
      /vad.*ska.*göra/,
      /nästa steg/,
      /next steps/,
      /what should i do/,
      /what now/,
    ],
  },
  {
    intent: "search_jobs",
    patterns: [
      /sök.*jobb/,
      /söka.*jobb/,
      /sök\s+efter/,
      /hitta.*jobb/,
      /sök\s+(\w+)/,
      /search.*job/,
      /find.*job/,
    ],
  },
];

export const getQuickQuestions = (): QuickQuestion[] => QUICK_QUESTIONS;

export const parseIntent = (input: string): ChatIntent => {
  const normalized = normalizeText(input);
  if (!normalized) return "fallback";

  const exact = EXACT_MATCHES.get(normalized);
  if (exact) return exact;

  for (const rule of INTENT_RULES) {
    if (rule.patterns.some((pattern) => pattern.test(normalized))) {
      return rule.intent;
    }
  }

  return "fallback";
};

function normalizeText(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[!?.,;:]/g, "")
    .replace(/\s+/g, " ");
}
